import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminOrganizationListResponse,
  IAdminOrganizationQueryParams,
  IAdminOrganizationActionResponse,
  IRejectOrganizationPayload,
  IRevokeOrganizationPayload,
  IAdminOrganizationResponse,
  IUpdateAdminOrganizationPayload,
  ICreateAdminOrganizationPayload,
  IUpdateOrganizationStatusPayload,
  IOrganizationStatusResponse,
  IOrganizationDocumentsQueryParams,
  IOrganizationDocumentsResponse,
  IOrganizationInvoicesQueryParams,
  IOrganizationInvoicesResponse,
  IMarkOrganizationInvoicePaidPayload,
  IMarkOrganizationInvoicePaidResponse,
  IDeleteOrganizationPayload,
  IDeleteOrganizationResponse,
} from '../../../interface/classveew-admin/organization.interface';
import { IErrorResponse } from '../../../interface/main.interface';
import { adminOrganizationService } from '../../../services/classveew-admin/organization';
import { normalizeAxiosError } from '../../../utility/axiosErrorHandler';

/**
 * Fetch all admin organizations with optional query parameters
 */
export const fetchAdminOrganizations = createAsyncThunk<
  IAdminOrganizationListResponse,
  IAdminOrganizationQueryParams | undefined,
  { rejectValue: IErrorResponse }
>('admin-organization/fetch-all', async (params, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.fetchOrganizations(params);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_fetch_admin_organizations', 'Failed to fetch admin organizations.')));
  }
});

/**
 * Create a new organization
 */
export const createAdminOrganization = createAsyncThunk<
  IAdminOrganizationResponse,
  ICreateAdminOrganizationPayload,
  { rejectValue: IErrorResponse }
>('admin-organization/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.createOrganization(payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_create_organization', 'Failed to create organization.')));
  }
});

/**
 * Approve an organization
 */
export const approveAdminOrganization = createAsyncThunk<
  IAdminOrganizationActionResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-organization/approve', async (organizationId, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.approveOrganization(organizationId);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_approve_organization', 'Failed to approve organization.')));
  }
});

/**
 * Reject an organization
 */
export const rejectAdminOrganization = createAsyncThunk<
  IAdminOrganizationActionResponse,
  { organizationId: string; payload: IRejectOrganizationPayload },
  { rejectValue: IErrorResponse }
>('admin-organization/reject', async ({ organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.rejectOrganization(organizationId, payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_reject_organization', 'Failed to reject organization.')));
  }
});

/**
 * Revoke an organization
 */
export const revokeAdminOrganization = createAsyncThunk<
  IAdminOrganizationActionResponse,
  { organizationId: string; payload: IRevokeOrganizationPayload },
  { rejectValue: IErrorResponse }
>('admin-organization/revoke', async ({ organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.revokeOrganization(organizationId, payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_revoke_organization', 'Failed to revoke organization.')));
  }
});

/**
 * Delete an institute request (soft archive or hard permanent delete).
 *
 * NOTE: `adminInstance` rejects with the RESPONSE BODY (not an AxiosError) via
 * its response interceptor, so in the catch `err` IS the body. We read
 * `body.message` directly and must NOT use `normalizeAxiosError` here.
 */
export const deleteAdminOrganization = createAsyncThunk<
  IDeleteOrganizationResponse,
  { organizationId: string; payload: IDeleteOrganizationPayload },
  { rejectValue: IErrorResponse }
>('admin-organization/delete', async ({ organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.deleteOrganization(organizationId, payload);
    return response.data;
  } catch (err) {
    const body = err as { message?: string };
    return rejectWithValue({ message: body?.message || 'Failed to delete request.' });
  }
});

/**
 * Get organization by ID
 */
export const getAdminOrganizationById = createAsyncThunk<
  IAdminOrganizationResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-organization/get-by-id', async (organizationId, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.getOrganizationById(organizationId);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_get_organization', 'Failed to get organization.')));
  }
});

/**
 * Update an organization
 */
export const updateAdminOrganization = createAsyncThunk<
  IAdminOrganizationResponse,
  { organizationId: string; payload: IUpdateAdminOrganizationPayload },
  { rejectValue: IErrorResponse }
>('admin-organization/update', async ({ organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.updateOrganization(organizationId, payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_update_organization', 'Failed to update organization.')));
  }
});

/**
 * Update organization status (active/inactive)
 */
export const updateOrganizationStatus = createAsyncThunk<
  IOrganizationStatusResponse,
  { organizationId: string; payload: IUpdateOrganizationStatusPayload },
  { rejectValue: IErrorResponse }
>('admin-organization/update-status', async ({ organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.updateOrganizationStatus(
      organizationId,
      payload,
    );
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_update_organization_status', 'Failed to update organization status.')));
  }
});

/**
 * Fetch organization documents
 */
export const fetchOrganizationDocuments = createAsyncThunk<
  IOrganizationDocumentsResponse,
  { organizationId: string; params?: IOrganizationDocumentsQueryParams },
  { rejectValue: IErrorResponse }
>(
  'admin-organization/fetch-documents',
  async ({ organizationId, params }, { rejectWithValue }) => {
    try {
      const response = await adminOrganizationService.getOrganizationDocuments(
        organizationId,
        params,
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        normalizeAxiosError(err, i18n.t('toast.failed_to_fetch_organization_documents', 'Failed to fetch organization documents.')),
      );
    }
  },
);

/**
 * Fetch organization invoices
 */
export const fetchOrganizationInvoices = createAsyncThunk<
  IOrganizationInvoicesResponse,
  { organizationId: string; params?: IOrganizationInvoicesQueryParams },
  { rejectValue: IErrorResponse }
>(
  'admin-organization/fetch-invoices',
  async ({ organizationId, params }, { rejectWithValue }) => {
    try {
      const response = await adminOrganizationService.getOrganizationInvoices(
        organizationId,
        params,
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        normalizeAxiosError(err, i18n.t('toast.failed_to_fetch_organization_invoices', 'Failed to fetch organization invoices.')),
      );
    }
  },
);

/**
 * Mark an organization invoice as paid (manual cash payment)
 */
export const markOrganizationInvoiceAsPaid = createAsyncThunk<
  IMarkOrganizationInvoicePaidResponse,
  IMarkOrganizationInvoicePaidPayload,
  { rejectValue: IErrorResponse }
>(
  'admin-organization/mark-invoice-paid',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await adminOrganizationService.markInvoiceAsPaid(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        normalizeAxiosError(err, i18n.t('toast.failed_to_mark_invoice_as_paid', 'Failed to mark invoice as paid.')),
      );
    }
  },
);

export const adminOrganizationActions = {
  fetchAdminOrganizations,
  createAdminOrganization,
  approveAdminOrganization,
  rejectAdminOrganization,
  revokeAdminOrganization,
  deleteAdminOrganization,
  getAdminOrganizationById,
  updateAdminOrganization,
  updateOrganizationStatus,
  fetchOrganizationDocuments,
  fetchOrganizationInvoices,
  markOrganizationInvoiceAsPaid,
};
